import { UserRoundX } from 'lucide-react'
import { useMemo } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'

import Avatar from '@/elements/Avatar'
import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Pill from '@/elements/Pill'
import Text from '@/elements/Text'
import { defaultColumnOptions, type TableAny } from '@/utils/tables'
import type { IndividualAssignmentResponse, SystemUserRolesType } from '@/api/types/Organization'
import useRoles from '@/hooks/useRoles'

interface IndividualDelegatesTableType {
  id: IndividualAssignmentResponse['id']
  avatarImg?: string
  name: IndividualAssignmentResponse['details']['fullName']
  role?: SystemUserRolesType
}

const columnHelper = createColumnHelper<IndividualDelegatesTableType>()

export function useIndividualDelegatesTable(
  delegates: IndividualAssignmentResponse[],
  onRemoveDelegate: (id: string) => void
) {
  const { getRoleName, getRoleColor } = useRoles()

  const data: IndividualDelegatesTableType[] = useMemo(() => (
    delegates ? delegates.map(row => ({
      id: row?.id,
      avatarImg: row?.assets?.PROFILE_PICTURE || undefined,
      name: row?.details?.fullName || '',
      role: row.roles[0]
    })) : []
  ), [delegates])

  const columns: ColumnDef<IndividualDelegatesTableType, TableAny>[] = [
    columnHelper.accessor('name', {
      ...defaultColumnOptions,
      header: 'Name',
      enableSorting: false,
      cell: info => (
        <Flex inline align='center' gap={2} className='user-cell'>
          <Avatar
            name={info.getValue() as string}
            src={info.row.original.avatarImg}
            size='extraSmall'
            color='gray'
          />
          <Text variant='textblock'>{info.getValue() as string}</Text>
        </Flex>
      )
    }),
    columnHelper.accessor('role', {
      ...defaultColumnOptions(),
      header: 'Role',
      enableSorting: false,
      cell: info => {
        const role = info.getValue() as SystemUserRolesType
        return (
          <Pill customColor={role ? getRoleColor(role) : 'undefined'}>
            {getRoleName(role) || 'N/A'}
          </Pill>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      ...defaultColumnOptions(),
      header: 'Actions',
      meta: { width: 100 },
      enableSorting: false,
      cell: info => {
        const delegateId = info.row.original.id
        return (
          <Flex justify='center' align='center' className='actions-cell'>
            <Button
              small
              variant='iconDangerBtn'
              onClick={() => onRemoveDelegate(delegateId)}
            >
              <UserRoundX size={18} />
            </Button>
          </Flex>
        )
      }
    }),
  ]

  return { data, columns }
}
